import React from 'react';
import {
    fmtMoney, fmtPct, fmtPp, fmtDelta, directionClass
} from '../utils/format';
import InsightsPanel from './InsightsPanel';

const MetricCell = ({ value, kind = 'money', digits = 2 }) => {
    if (value === null || value === undefined || isNaN(value)) {
        return <td className="fr-num fr-empty-cell">—</td>;
    }
    return (
        <td className="fr-num">
            {kind === 'pct' ? fmtPct(value, digits) : fmtMoney(value)}
        </td>
    );
};

const VarianceCell = ({ variance, upIsGood = true }) => {
    if (!variance || variance.abs === undefined || variance.abs === null) {
        return <td className="fr-num">—</td>;
    }
    const cls = directionClass(variance.abs, { upIsGood });
    return (
        <td className={`fr-num ${cls}`}>
            {fmtDelta(variance.abs)}
            {variance.pct !== null && variance.pct !== undefined && (
                <span className="fr-yc-var-pct"> ({fmtPct(variance.pct, 1)})</span>
            )}
        </td>
    );
};

const TotalsKpi = ({ label, a, b, variance, kind = 'money', upIsGood = true, yearA, yearB }) => {
    const cls = variance ? directionClass(variance.abs ?? variance, { upIsGood }) : 'fr-neutral';
    return (
        <div className={`fr-kpi-card ${cls}`}>
            <div className="fr-kpi-label">{label}</div>
            <div className="fr-kpi-value">{kind === 'pct' ? fmtPct(a) : fmtMoney(a)}</div>
            <div className="fr-kpi-baseline">
                vs <strong>{kind === 'pct' ? fmtPct(b) : fmtMoney(b)}</strong> · FY{yearB}
            </div>
            <div className="fr-kpi-delta">
                {kind === 'pct'
                    ? fmtPp(variance) // when caller passes a pp number directly
                    : (variance ? (
                        <>
                            {fmtDelta(variance.abs)}
                            {variance.pct !== null && variance.pct !== undefined && ` · ${fmtPct(variance.pct, 1)}`}
                        </>
                    ) : '—')}
            </div>
        </div>
    );
};

const LineItemSection = ({ title, rows, expensesNegativeIsGood = false }) => {
    if (!rows || rows.length === 0) {
        return (
            <div className="fr-card">
                <div className="fr-card-header"><h3>{title}</h3></div>
                <div className="fr-empty-cell">No comparable line items.</div>
            </div>
        );
    }
    return (
        <div className="fr-card">
            <div className="fr-card-header"><h3>{title}</h3></div>
            <div className="fr-table-wrap">
                <table className="fr-table">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th className="fr-num">Current</th>
                            <th className="fr-num">Baseline</th>
                            <th className="fr-num">Variance</th>
                            <th className="fr-num">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const upIsGood = !expensesNegativeIsGood; // income up = good; expenses up = bad
                            const cls = directionClass(r.deltaAmount, { upIsGood });
                            return (
                                <tr key={r.name}>
                                    <td><strong>{r.name}</strong></td>
                                    <td className="fr-num">{fmtMoney(r.a)}</td>
                                    <td className="fr-num">{fmtMoney(r.b)}</td>
                                    <td className={`fr-num ${cls}`}>{fmtDelta(r.deltaAmount)}</td>
                                    <td className={`fr-num ${cls}`}>
                                        {r.deltaPercent === null || r.deltaPercent === undefined ? '—' : fmtPct(r.deltaPercent, 1)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const YearComparisonView = ({ data }) => {
    if (!data) return null;

    const {
        businessUnit, yearA, yearB,
        months, totalsA, totalsB,
        comparable, lineItemTotals, insights
    } = data;

    const cmp = comparable || { totalsA: {}, totalsB: {}, variance: {}, monthsIncluded: [] };
    const v = cmp.variance || {};
    const cmpA = cmp.totalsA || {};
    const cmpB = cmp.totalsB || {};

    const comparableLabel = cmp.monthsIncluded?.length
        ? `${cmp.monthsIncluded.length} month${cmp.monthsIncluded.length === 1 ? '' : 's'} compared`
        : 'No overlapping months';

    return (
        <div className="fr-yc">
            {/* Banner */}
            <section className="fr-card fr-yc-banner">
                <div>
                    <h2>FY{yearA} <span className="fr-yc-vs">vs</span> FY{yearB}</h2>
                    <p>{businessUnit}</p>
                </div>
                <div className="fr-yc-banner-meta">
                    <span className="fr-badge fr-badge-approved">{comparableLabel}</span>
                    <small>
                        FY{yearA}: {totalsA?.monthsIncluded?.length || 0} mo uploaded ·
                        {' '}FY{yearB}: {totalsB?.monthsIncluded?.length || 0} mo uploaded
                    </small>
                </div>
            </section>

            {/* Apples-to-apples KPI strip */}
            {cmp.monthsIncluded?.length > 0 && (
                <section className="fr-card">
                    <div className="fr-card-header">
                        <h3>Year-over-year totals (apples-to-apples)</h3>
                        <p>Summed across months present in both years: {cmp.monthsIncluded.map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1]).join(', ')}.</p>
                    </div>
                    <div className="fr-kpi-strip">
                        <TotalsKpi label="Sales"             a={cmpA.sales}            b={cmpB.sales}            variance={v.sales}            yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Direct Expenses"   a={cmpA.directExpenses}   b={cmpB.directExpenses}   variance={v.directExpenses}   upIsGood={false} yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Gross Profit"      a={cmpA.grossProfit}      b={cmpB.grossProfit}      variance={v.grossProfit}      yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Gross Margin"      a={cmpA.grossMargin}      b={cmpB.grossMargin}      variance={v.grossMarginPp}    kind="pct" yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Indirect Expenses" a={cmpA.indirectExpenses} b={cmpB.indirectExpenses} variance={v.indirectExpenses} upIsGood={false} yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Net Profit"        a={cmpA.netProfit}        b={cmpB.netProfit}        variance={v.netProfit}        yearA={yearA} yearB={yearB} />
                        <TotalsKpi label="Net Margin"        a={cmpA.netMargin}        b={cmpB.netMargin}        variance={v.netMarginPp}      kind="pct" yearA={yearA} yearB={yearB} />
                    </div>
                </section>
            )}

            {/* Insights */}
            {insights?.length > 0 && (
                <InsightsPanel
                    insights={insights}
                    periodA={{ label: `FY${yearA}` }}
                    periodB={{ label: `FY${yearB}` }}
                />
            )}

            {/* Month-by-month table */}
            <section className="fr-card">
                <div className="fr-card-header">
                    <h3>Month-by-month breakdown</h3>
                    <p>All 12 months side-by-side. Variance compares FY{yearA} to FY{yearB} where both have data.</p>
                </div>
                <div className="fr-table-wrap">
                    <table className="fr-table fr-yc-month-table">
                        <thead>
                            <tr>
                                <th rowSpan={2}>Month</th>
                                <th colSpan={3}>Sales</th>
                                <th colSpan={3}>Gross Profit</th>
                                <th colSpan={3}>Indirect Exp.</th>
                                <th colSpan={3}>Net Profit</th>
                            </tr>
                            <tr>
                                <th className="fr-num">FY{yearA}</th>
                                <th className="fr-num">FY{yearB}</th>
                                <th className="fr-num">Δ</th>
                                <th className="fr-num">FY{yearA}</th>
                                <th className="fr-num">FY{yearB}</th>
                                <th className="fr-num">Δ</th>
                                <th className="fr-num">FY{yearA}</th>
                                <th className="fr-num">FY{yearB}</th>
                                <th className="fr-num">Δ</th>
                                <th className="fr-num">FY{yearA}</th>
                                <th className="fr-num">FY{yearB}</th>
                                <th className="fr-num">Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map(row => {
                                const a = row.yearA;
                                const b = row.yearB;
                                const var_ = row.variance;
                                const bothEmpty = !a.hasData && !b.hasData;
                                if (bothEmpty) {
                                    return (
                                        <tr key={row.month} className="fr-yc-month-empty">
                                            <td><strong>{row.monthLabel}</strong></td>
                                            <td colSpan={12} className="fr-empty-cell">No data uploaded for either year</td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr key={row.month}>
                                        <td><strong>{row.monthLabel}</strong></td>

                                        <MetricCell value={a.hasData ? a.sales : null} />
                                        <MetricCell value={b.hasData ? b.sales : null} />
                                        <VarianceCell variance={var_?.sales} />

                                        <MetricCell value={a.hasData ? a.grossProfit : null} />
                                        <MetricCell value={b.hasData ? b.grossProfit : null} />
                                        <VarianceCell variance={var_?.grossProfit} />

                                        <MetricCell value={a.hasData ? a.indirectExpenses : null} />
                                        <MetricCell value={b.hasData ? b.indirectExpenses : null} />
                                        <VarianceCell variance={var_?.indirectExpenses} upIsGood={false} />

                                        <MetricCell value={a.hasData ? a.netProfit : null} />
                                        <MetricCell value={b.hasData ? b.netProfit : null} />
                                        <VarianceCell variance={var_?.netProfit} />
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="fr-cmp-total">
                                <td><strong>FY Total (uploaded)</strong></td>
                                <td className="fr-num">{fmtMoney(totalsA?.sales)}</td>
                                <td className="fr-num">{fmtMoney(totalsB?.sales)}</td>
                                <td className="fr-num">—</td>
                                <td className="fr-num">{fmtMoney(totalsA?.grossProfit)}</td>
                                <td className="fr-num">{fmtMoney(totalsB?.grossProfit)}</td>
                                <td className="fr-num">—</td>
                                <td className="fr-num">{fmtMoney(totalsA?.indirectExpenses)}</td>
                                <td className="fr-num">{fmtMoney(totalsB?.indirectExpenses)}</td>
                                <td className="fr-num">—</td>
                                <td className="fr-num">{fmtMoney(totalsA?.netProfit)}</td>
                                <td className="fr-num">{fmtMoney(totalsB?.netProfit)}</td>
                                <td className="fr-num">—</td>
                            </tr>
                            <tr className="fr-cmp-total fr-yc-cmp-row">
                                <td><strong>Apples-to-apples</strong></td>
                                <td className="fr-num">{fmtMoney(cmpA.sales)}</td>
                                <td className="fr-num">{fmtMoney(cmpB.sales)}</td>
                                <VarianceCell variance={v.sales} />
                                <td className="fr-num">{fmtMoney(cmpA.grossProfit)}</td>
                                <td className="fr-num">{fmtMoney(cmpB.grossProfit)}</td>
                                <VarianceCell variance={v.grossProfit} />
                                <td className="fr-num">{fmtMoney(cmpA.indirectExpenses)}</td>
                                <td className="fr-num">{fmtMoney(cmpB.indirectExpenses)}</td>
                                <VarianceCell variance={v.indirectExpenses} upIsGood={false} />
                                <td className="fr-num">{fmtMoney(cmpA.netProfit)}</td>
                                <td className="fr-num">{fmtMoney(cmpB.netProfit)}</td>
                                <VarianceCell variance={v.netProfit} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </section>

            {/* Line item rollups */}
            <div className="fr-yc-line-grid">
                <LineItemSection title="Direct Income — by category"      rows={lineItemTotals?.directIncome} />
                <LineItemSection title="Direct Expenses — by category"    rows={lineItemTotals?.directExpenses}    expensesNegativeIsGood />
                <LineItemSection title="Indirect Expenses — by category"  rows={lineItemTotals?.indirectExpenses}  expensesNegativeIsGood />
                <LineItemSection title="Indirect Income — by category"    rows={lineItemTotals?.indirectIncome} />
            </div>
        </div>
    );
};

export default YearComparisonView;
